<template>
  <div class="row" id="parentConsentCheckbox">
    <Checkbox
      ref="checkboxRef"
      :name="schema.parentConsent.name || 'parentConsent'"
      v-model="parentConsent"
      :schema="schema.parentConsent"
      :disabled="disabled"
      @change="handleChange(parentConsent)"
    >
      <template #label>
        {{ schema.parentConsent.label }}
      </template>
    </Checkbox>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Checkbox from "@/components/inputs/Checkbox.vue";
import { ConsentTypeIds } from "@/config/constants";
import type { FieldSchema, FormSchema } from "@/models/form";
import { useStore } from "vuex";

const checkboxRef = ref<InstanceType<typeof Checkbox>>();

const store = useStore();

const consent = computed(() => store.state.loanConfigurationModule.studentInfo.parentAttestation);

const props = withDefaults(
  defineProps<{
    disabled: boolean;
    modelValue: boolean | undefined;
    entityTypeId: number;
    entityId: number;
  }>(),
  {
    disabled: false,
    modelValue: undefined,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: undefined | boolean): void;
}>();

const parentConsent = ref<boolean | undefined>(undefined);
const parentConsentId = ref<number>();

const errors = computed<Record<string, string>>(() => checkboxRef.value?.errors || {});

const schema = ref<FormSchema>({
  parentConsent: {
    name: "parentConsent",
    type: "checkbox",
    label:
      "By checking this box, you affirm that you are the student's parent or legal guardian and that you claim the student as a dependent on your tax return.",
    errorMessage: "You must check the box to continue.",
    required: true,
    validate: (schema: FieldSchema) => {
      schema.valid = !errors.value.parentConsent;
      schema.errors = errors.value.parentConsent ? [errors.value.parentConsent] : [];

      if (!schema.valid) {
        schema.touched = true;
      } else {
        schema.valid = undefined;
        schema.errors = [];
      }
    },
  } as FieldSchema,
} as FormSchema);

function handleChange(consentGiven: undefined | boolean) {
  emit("update:modelValue", consentGiven);
}

async function getParentAttestationConsents() {
  const parentConsentTypeId = ConsentTypeIds.PARENT_ATTESTATION;

  parentConsentId.value = parentConsentTypeId;

  if (consent.value) {
    parentConsent.value = consent.value?.consentGiven;
    parentConsentId.value = consent.value?.id;
    emit("update:modelValue", consent.value?.consentGiven);
  } else {
    emit("update:modelValue", undefined);
  }
}

watch(
  () => props.modelValue,
  () => {
    parentConsent.value = props.modelValue;
  },
  {
    immediate: true,
  },
);

defineExpose({
  errors,
  getParentAttestationConsents,
  parentConsent,
});
</script>

<style scoped>
.consent-details {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
}
.consent-detail span:first-of-type {
  font-weight: 800;
}

.cuscd-full-wrap .row#parentConsentCheckbox {
  margin-right: 0;
  margin-left: 0;
  margin-top: 1rem;
}
</style>
